import { useCallback, useEffect, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { endOfMonth } from 'date-fns';
import { KpiPeriod } from '../../../../data-models/company-financials.data-model';
import { FMT, formatISODateOnly, getFiscalQuarter } from '@foresightdata/web-common-formatter-service';
import { selectedMetricsDateCPState } from '../../state/CompanyFinancialsDateState';
import { selectedCompanyIdProfileAtom } from '../../state/UIStateJ';
import { perfFrequencyAtom } from '../../state/FinancialsDateViewPreferences';
import {
  calendarMonthOfFiscalQuarter,
  endOfFiscalPeriod,
  getFormattedFiscalDate,
} from '../../utils/financialUtils';

export function useFiscalDatePicker({ fye }: { fye: number }) {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);

  const fyeMonth = fye - 1;
  const [frequency, setFrequency] = useAtom(perfFrequencyAtom(companyId));
  const [date, setDate] = useAtom(selectedMetricsDateCPState(companyId));

  // Snap the raw date to the correct fiscal period end for the initial frequency.
  // The frequency now comes from the org/company preference (via perfFrequencyAtom),
  // but the date defaults to the latest data point, which may be from a different
  // period granularity.
  const initialDate = endOfFiscalPeriod(date, frequency, fyeMonth);
  const [month, setMonth] = useState<number>(() => initialDate.getMonth());
  const [quarter, setQuarter] = useState<number>(
    () => getFiscalQuarter(initialDate, fyeMonth + 1).fiscalQuarter
  );
  const [year, setYear] = useState<number>(() => initialDate.getFullYear());

  const onChangeFrequency = useCallback(
    (freq: KpiPeriod) => {
      setFrequency(freq);
      const newDate = endOfFiscalPeriod(date, freq, fyeMonth);
      setMonth(newDate.getMonth());
      setQuarter(getFiscalQuarter(newDate, fyeMonth + 1).fiscalQuarter);
      setYear(newDate.getFullYear());
      setDate(newDate);
    },
    [date, fyeMonth, setDate, setFrequency]
  );

  useEffect(() => {
    switch (frequency) {
      case KpiPeriod.month: {
        setDate(endOfMonth(new Date(year, month)));
        break;
      }
      case KpiPeriod.quarter: {
        setDate(endOfMonth(new Date(year, calendarMonthOfFiscalQuarter(quarter, fyeMonth))));
        break;
      }
      case KpiPeriod.year: {
        setDate(endOfMonth(new Date(year, fyeMonth)));
        break;
      }
    }
  }, [frequency, fye, fyeMonth, month, quarter, setDate, year]);

  const label = `As Of: ${FMT.format('dateNumeric', date)}${
    frequency === KpiPeriod.month || fye === 12
      ? ''
      : ` (${getFormattedFiscalDate(formatISODateOnly(date), frequency, fye)})`
  }`;
  return { frequency, onChangeFrequency, label, year, month, quarter, setMonth, setQuarter, setYear, date };
}
